import { useState } from 'react';
import { Copy, Check, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';
import { DEMO_CREDENTIALS } from '../utils/demoAuth';
import { toast } from 'sonner';

export function DemoCredentials() {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const copyToClipboard = (text: string, index: number) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          setCopiedIndex(index);
          toast.success('Copied to clipboard!');
          setTimeout(() => setCopiedIndex(null), 2000);
        }).catch(() => {
          // Fallback to legacy method
          fallbackCopy(text, index);
        });
      } else {
        // Use fallback for non-secure contexts
        fallbackCopy(text, index);
      }
    } catch (error) {
      // Use fallback on any error
      fallbackCopy(text, index);
    }
  };

  const fallbackCopy = (text: string, index: number) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      setCopiedIndex(index);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      toast.error('Failed to copy. Please copy manually.');
    } finally {
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-4 mt-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center">
            <Eye className="h-4 w-4 text-white" />
          </div>
          <div className="text-left">
            <p className="font-bold text-purple-900">Demo Mode Available</p>
            <p className="text-xs text-purple-700">Click to view test credentials</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-purple-600" />
        ) : (
          <ChevronDown className="h-5 w-5 text-purple-600" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-3 pt-4 border-t border-purple-200">
          <p className="text-xs text-purple-800 mb-3">
            Use these credentials to login and explore the app with dummy data:
          </p>

          {DEMO_CREDENTIALS.map((cred, index) => (
            <div key={index} className="bg-white rounded-xl p-3 border border-purple-100">
              <div className="flex items-start gap-3">
                <div 
                  className="w-12 h-12 rounded-full bg-cover bg-center ring-2 ring-purple-200"
                  style={{ 
                    backgroundImage: `url('https://images.unsplash.com/photo-${
                      index === 0 ? '1544005313-94ddf0286df2' : 
                      index === 1 ? '1507003211169-0a1dd7228f2d' : 
                      '1534528741775-53994a69daeb'
                    }?w=100')` 
                  }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 mb-2">{cred.name}</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2 text-sm font-mono text-gray-700 truncate">
                        {cred.email}
                      </div>
                      <button
                        onClick={() => copyToClipboard(cred.email, index * 2)}
                        className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
                      >
                        {copiedIndex === index * 2 ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4 text-purple-600" />
                        )}
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2 text-sm font-mono text-gray-700">
                        {cred.password}
                      </div>
                      <button
                        onClick={() => copyToClipboard(cred.password, index * 2 + 1)}
                        className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
                      >
                        {copiedIndex === index * 2 + 1 ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4 text-purple-600" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="bg-purple-100 rounded-lg p-3 mt-4">
            <p className="text-xs text-purple-900 font-semibold mb-2">✨ What you'll see:</p>
            <ul className="text-xs text-purple-800 space-y-1">
              <li>• Complete user profiles with photos</li>
              <li>• 5+ match profiles to browse</li>
              <li>• Active conversations with messages</li>
              <li>• Interest requests & shortlists</li>
              <li>• All app features with dummy data</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}